import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { permissionsKey } from '../../utils/permissionsKey';
import { useDashboardAccess } from '../dashboard/useDashboardAccess';
import { HOME_PATH, landingPathFor } from '../../utils/roleLanding';
import { MERCH_TILES_PATH, useDashboardStyle } from '../../context/DashboardStyleContext';

/** Give up waiting for permissions/modules after this long and land on the overview. */
const FALLBACK_MS = 8000;

/**
 * Post-login entry point (/home): waits until the effective permission set has been loaded FOR THE
 * CURRENT user + institution (not merely `permissionsReady`, which can be a stale `true` for a
 * render right after login / impersonation / an institution switch) and the enabled-modules answer
 * is for the same institution, then replaces itself with the dashboard for the user's role (see
 * utils/roleLanding). An empty set that was genuinely loaded for this identity is a real answer
 * (plain employee, or a failed fetch that failed closed) and lands on the overview.
 */
const RoleLanding: React.FC = () => {
    const {
        user, isAdmin, isSystemAdmin, hasPermission, permissionsReady, permissionsLoadedFor, selectedInstitutionId,
        showInstitutionModal,
    } = useAuth();
    const { ready: accessReady, enabledModules, visibleModules, modulesInstitutionId } = useDashboardAccess();
    const dashboardStyle = useDashboardStyle();
    const [timedOut, setTimedOut] = useState(false);

    // A multi-institution user is still picking in the institution modal: that's the user's own
    // pace, not a slow network, so the fallback clock only runs once nothing is left to pick.
    useEffect(() => {
        if (showInstitutionModal) return;
        const t = setTimeout(() => setTimedOut(true), FALLBACK_MS);
        return () => clearTimeout(t);
    }, [showInstitutionModal]);

    const institutionId = selectedInstitutionId ?? null;
    const permissionsCurrent = permissionsReady && !!user
        && permissionsLoadedFor === permissionsKey(user.username, institutionId);
    const modulesCurrent = accessReady && modulesInstitutionId === institutionId;
    const ready = permissionsCurrent && modulesCurrent && !showInstitutionModal;
    const jwtRoles = (Array.isArray(user?.roles) ? user!.roles : String(user?.roles ?? '').split(','))
        .map(r => String(r).trim());

    if (ready) {
        const target = landingPathFor({
            isSystemAdmin,
            isPlatformOperator: jwtRoles.includes('SaaSOperator'),
            isAdmin,
            isDepartmentAdmin: user?.isDepartmentAdmin === true,
            has: hasPermission,
            moduleEnabled: m => (enabledModules as Set<string>).has(m),
            boardVisible: m => (visibleModules as Set<string>).has(m),
        });
        // A Thread-enabled home: the saved pick decides the landing. Thread lives on /, Tiles keeps the role landing path
        // (for a merchandiser Tiles is the Merchandising Dashboard). Wait for the saved pick only when it can change the
        // outcome: a redirected landing, or a merchandiser (who defaults to Thread).
        // A merchandiser only differs when the landing is `/` or the merch page: a custom role that also routes to another
        // board (for example people) keeps that landing.
        const merchLanding = target === HOME_PATH || target === MERCH_TILES_PATH;
        const mayDiffer = dashboardStyle.group !== null
            && (dashboardStyle.group === 'merch' ? merchLanding : target !== HOME_PATH);
        if (mayDiffer) {
            if (dashboardStyle.ready) return <Navigate to={dashboardStyle.style === 'THREAD' ? HOME_PATH : target} replace />;
        } else {
            return <Navigate to={target} replace />;
        }
    }

    if (timedOut && !showInstitutionModal) return <Navigate to={HOME_PATH} replace />;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }} role="status" aria-label="Loading your dashboard">
            <CircularProgress />
        </Box>
    );
};

export default RoleLanding;
